import { ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";

import { cn } from "@/utils";
import { Button } from "@/components";
import { CloseMD } from "@/icons";
import { useTranslate } from "@/providers/context";

const COLS: Record<number, string> = {
  1: "cols-1",
  2: "cols-2",
  3: "cols-3",
  4: "cols-4",
  5: "cols-5",
};

interface ColumnModalProps {
  title?: string;
  titleClassName?: string;
  cols: 1 | 2 | 3 | 4 | 5; // Number of columns, determines modal width
  triggerButton?: ReactNode;
  hideTrigger?: boolean;
  children?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  danger?: boolean;

  // Use default actions
  onConfirm?: () => void; // Complete the modal
  confirmDisabled?: boolean; // Block proceeding on conditions
  confirmIsLoading?: boolean; // Manage loading state of confirm
  onCancel?: () => void; // Cancel the modal
  hideClose?: boolean; // Hide X button
  hideFooter?: boolean;
  closeButtonClassName?: string;
  primaryText?: string;
  secondaryText?: string;

  align?: "left" | "right" | "full"; // Align buttons or make them full width
  contentClassName?: string; // Use custom padding for content section of modal
  maxHeight?: string;
}

export const ColumnModal = ({
  cols,
  title,
  titleClassName,
  triggerButton,
  hideTrigger,
  contentClassName,
  maxHeight,
  hideClose,
  hideFooter,
  closeButtonClassName,
  primaryText,
  secondaryText,
  children,
  onConfirm,
  onCancel,
  confirmDisabled,
  confirmIsLoading,
  open,
  onOpenChange,
  danger,
  align,
}: ColumnModalProps) => {
  const { translate } = useTranslate();
  const primaryButtonText = primaryText ?? "Ok";

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange} modal>
      {!hideTrigger && (
        <Dialog.Trigger asChild={!!triggerButton}>
          {triggerButton ?? "Open Modal"}
        </Dialog.Trigger>
      )}
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[70] grid place-items-center overflow-y-auto bg-black bg-opacity-50" />
        <Dialog.Content
          className={cn([
            "fixed left-1/2 top-1/2 z-[99] max-w-[90%] -translate-x-1/2 -translate-y-1/2 transform pt-[24px]",
            "rounded-[16px] bg-surface-primary",
            COLS[cols],
          ])}
          style={{ maxHeight: maxHeight ?? "700px" }}
          onPointerDownOutside={(e) => {
            e.preventDefault();
          }}
          onInteractOutside={(e) => {
            e.preventDefault();
          }}
        >
          {title && (
            <Dialog.Title className="p2-medium mb-[8px] flex w-full justify-between px-[24px]">
              <div
                className={cn(
                  "p1-bold max-w-full overflow-hidden overflow-ellipsis whitespace-nowrap text-item-primary",
                  titleClassName,
                )}
              >
                {title}
              </div>
            </Dialog.Title>
          )}
          {!hideClose && (
            <Dialog.Close
              asChild
              className={cn([
                "absolute right-[24px] top-[24px] z-10 cursor-pointer rounded-3xl text-[18px] hover:text-item-tertiary",
                closeButtonClassName,
              ])}
              onClick={onCancel}
            >
              <div className="text-item-secondary">
                <CloseMD className="h-[24px] w-[24px]" />
              </div>
            </Dialog.Close>
          )}
          <div className={contentClassName ?? ""}>{children}</div>
          {!hideFooter && (
            <div
              className={cn([
                "flex px-[24px] pb-[24px]",
                align === "left" ? "justify-start" : "justify-end",
              ])}
            >
              {onCancel && (
                <Button
                  onClick={onCancel}
                  size="m"
                  variant="secondary"
                  className="p2-semibold mr-3"
                >
                  {secondaryText || translate("Cancel")}
                </Button>
              )}
              {/* When using open we are a controlled modal, need to setOpen(false) during onConfirm */}
              <Button
                onClick={onConfirm}
                loading={confirmIsLoading}
                disabled={confirmDisabled}
                size="m"
                variant={danger ? "danger" : "primary"}
                className={cn([
                  "p2-semibold",
                  align === "full" ? "w-full" : "",
                ])}
              >
                {primaryButtonText}
              </Button>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
